import React, { useState, useMemo, useEffect } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { Content } from './style'
import {
  EMPTY_PRODUCT_FILTERS,
  type ProductDevelopmentProductFilters,
} from '../ProductDevelopment/productDevelopmentFilters'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { Filter } from '../../../components/SubHeaderFilter/types'

const COMPOSITION_VIEW_MODE_STORAGE_KEY = 'product-development-composition-view-mode'

interface TypeMainOutlet {
  search: string
}

export interface ProductCompositionOutletType {
  refresh: boolean
  search: string
  viewMode: 'board' | 'table'
  productFilters: ProductDevelopmentProductFilters
  appliedFilters: Filter[]
}

const ProductCompositionContent = () => {
  const [refresh, setRefresh] = useState(false)
  const [viewMode, setViewMode] = useState<'board' | 'table'>(() => {
    const persistedMode = localStorage.getItem(COMPOSITION_VIEW_MODE_STORAGE_KEY)
    return persistedMode === 'board' ? 'board' : 'table'
  })
  const [productFilters, setProductFilters] = useState<ProductDevelopmentProductFilters>(
    EMPTY_PRODUCT_FILTERS,
  )
  const mainOutletContext = useOutletContext<TypeMainOutlet | undefined>()
  const search = mainOutletContext?.search || ''

  const context = useOutletContext<any>()
  const fallbackFiltersState = usePersistedFilters('composition_product')
  const compositionFiltersState = context?.compositionFiltersState || fallbackFiltersState

  useEffect(() => {
    const handleProductDevelopmentRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string }>).detail
      if (detail?.tab === 'composition') {
        setRefresh(previous => !previous)
      }
    }

    const handleProductDevelopmentViewMode = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string; viewMode?: 'board' | 'table' }>).detail
      if (detail?.tab !== 'composition') {
        return
      }

      if (detail?.viewMode === 'board' || detail?.viewMode === 'table') {
        setViewMode(detail.viewMode)
      }
    }

    const handleProductDevelopmentFilters = (event: Event) => {
      const detail = (event as CustomEvent<{ filters?: ProductDevelopmentProductFilters }>).detail
      if (!detail?.filters) {
        return
      }
      setProductFilters(detail.filters)
    }

    window.addEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
    window.addEventListener('product-development-view-mode', handleProductDevelopmentViewMode as EventListener)
    window.addEventListener('product-development-filters', handleProductDevelopmentFilters as EventListener)

    return () => {
      window.removeEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
      window.removeEventListener('product-development-view-mode', handleProductDevelopmentViewMode as EventListener)
      window.removeEventListener('product-development-filters', handleProductDevelopmentFilters as EventListener)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(COMPOSITION_VIEW_MODE_STORAGE_KEY, viewMode)
  }, [viewMode])

  const outletContext: ProductCompositionOutletType = useMemo(
    () => ({
      refresh,
      search,
      viewMode,
      productFilters,
      appliedFilters: compositionFiltersState.appliedFilters,
    }),
    [refresh, search, viewMode, productFilters, compositionFiltersState.appliedFilters],
  )

  return (
    <Content>
      <Outlet context={outletContext} />
    </Content>
  )
}

export default ProductCompositionContent
